import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { adaptOutputContract } from "../adapters/outputContractAdapter";
import LCAResults from "../components/LCAResults";

function ResultsInterpretation() {
  const navigate = useNavigate();
  const location = useLocation();
  const rawOutputContract = location.state?.outputContract || null;
  const adaptedOutputContract =
    rawOutputContract && typeof rawOutputContract === "object"
      ? adaptOutputContract(rawOutputContract)
      : null;

  const outputContract =
    rawOutputContract && adaptedOutputContract
      ? {
          ...rawOutputContract,
          ...adaptedOutputContract,
          summary: adaptedOutputContract.summary || rawOutputContract.summary,
          results: adaptedOutputContract.results || rawOutputContract.results,
          totals: adaptedOutputContract.totals || rawOutputContract.totals,
        }
      : rawOutputContract;

  // SAFE DATA EXTRACTION BLOCK
  const safeOutput = outputContract || {};

  const coverage = safeOutput.coverage || {};
  const totalRecords = Number(coverage.total_records || 0);
  const governedCount = Number(coverage.governed_count || 0);
  const excludedCount = Number(coverage.excluded_count || 0);

  const governedRatioRaw =
    typeof coverage.governed_ratio === "number"
      ? coverage.governed_ratio
      : totalRecords > 0
      ? governedCount / totalRecords
      : 0;

  const governedRatioPercent = Math.round(governedRatioRaw * 1000) / 10;

  // CORRECT DATA SOURCE
  const totalA13 =
    safeOutput?.summary?.total_kg_co2e ??
    safeOutput?.summary?.total_a1_a3 ??
    safeOutput?.summary?.gwp?.total_a1_a3 ??
    safeOutput?.summary?.gwp?.A1_A3 ??
    safeOutput?.totals?.a1_a3 ??
    safeOutput?.totals?.A1_A3 ??
    safeOutput?.module_totals?.A1_A3 ??
    safeOutput?.stage_totals?.A1_A3 ??
    null;

  const datasetLabel =
    safeOutput?.metadata?.dataset_name ||
    safeOutput?.audit?.dataset_mix?.primary_dataset ||
    "Governed dataset";

  const byStage = Array.isArray(safeOutput?.breakdown?.by_stage)
    ? safeOutput.breakdown.by_stage
        .filter((row) => ["A1", "A2", "A3"].includes(String(row.stage || "").toUpperCase()))
        .filter((row) => Number(row.kg_co2e || 0) > 0)
    : [];

  const byMaterial = Array.isArray(safeOutput?.breakdown?.by_material_category)
    ? safeOutput.breakdown.by_material_category
        .filter((row) => Number(row.kg_co2e || 0) > 0)
        .sort((a, b) => Number(b.kg_co2e || 0) - Number(a.kg_co2e || 0))
    : [];

  const topMaterials = byMaterial.slice(0, 3);

  const totalA13Display =
    totalA13 !== null && totalA13 !== undefined
      ? Number(totalA13).toFixed(2)
      : "N/A";

  const donutStyle = {
    width: 140,
    height: 140,
    borderRadius: "50%",
    background: `conic-gradient(#84cc16 0 ${governedRatioPercent}%, #cbd5e1 ${governedRatioPercent}% 100%)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px auto",
  };

  const donutInnerStyle = {
    width: 90,
    height: 90,
    borderRadius: "50%",
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: "24px",
    color: "#23395d",
  };

  if (!outputContract) {
    return (
      <div style={{ display: "flex", minHeight: "100vh", background: "#0f172a" }}>
        <div
          style={{
            width: 240,
            background: "#020617",
            color: "#ffffff",
            padding: "24px 16px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 24 }}>BuildCarbonIQ</div>

          <div style={{ opacity: 0.9, marginBottom: 12 }}>Dashboard</div>
          <div style={{ opacity: 0.6, marginBottom: 12 }}>Projects</div>
          <div style={{ opacity: 0.6, marginBottom: 12 }}>Reports</div>
          <div style={{ opacity: 0.6 }}>Datasets</div>
        </div>
        <div
          style={{
            maxWidth: "1100px",
            margin: "0 auto",
            background: "#ffffff",
            border: "1px solid #e6ecf5",
            borderRadius: 18,
            padding: 32,
            boxShadow: "0 10px 28px rgba(0,0,0,0.06)",
          }}
        >
          <h1 style={{ margin: 0, fontSize: "28px", fontWeight: 600, color: "#14223b" }}>
            Results Interpretation
          </h1>

          <p style={{ marginTop: 0, marginBottom: 24, color: "#5f6f86" }}>
            No live run data was passed to this page.
          </p>

          <button
            onClick={() => navigate("/dashboard")}
            style={{
              background: "linear-gradient(135deg, #1e40af, #2563eb)",
              color: "#ffffff",
              border: "none",
              padding: "12px 18px",
              borderRadius: 8,
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#07152d" }}>
      <div
        style={{
          width: 260,
          background: "linear-gradient(180deg, #0b2347 0%, #07152d 100%)",
          color: "#ffffff",
          padding: "28px 18px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <div>
          <div style={{ marginBottom: 36 }}>
            <div style={{ position: "relative", display: "inline-flex" }}>
              <div
                style={{
                  position: "absolute",
                  inset: "-12px -16px",
                  background:
                    "radial-gradient(circle, rgba(56,189,248,0.18), rgba(15,23,42,0) 70%)",
                  filter: "blur(8px)",
                }}
              />
              <img
                src="/buildcarboniq-logo.png"
                alt="BuildCarbonIQ"
                style={{
                  width: 220,
                  height: "auto",
                  position: "relative",
                  zIndex: 1,
                  filter: "drop-shadow(0 8px 20px rgba(0,0,0,0.25))",
                }}
              />
            </div>
          </div>

          <div
            style={{
              padding: "12px 14px",
              borderRadius: 10,
              background: "rgba(255,255,255,0.10)",
              marginBottom: 12,
              fontWeight: 600,
            }}
          >
            Dashboard
          </div>

          <div style={{ padding: "12px 14px", opacity: 0.78, marginBottom: 12 }}>
            Projects
          </div>

          <div style={{ padding: "12px 14px", opacity: 0.78, marginBottom: 12 }}>
            Reports
          </div>

          <div style={{ padding: "12px 14px", opacity: 0.78 }}>
            Datasets
          </div>
        </div>

        <div
          style={{
            paddingTop: 20,
            borderTop: "1px solid rgba(255,255,255,0.10)",
            fontSize: 13,
            lineHeight: 1.8,
            opacity: 0.9,
          }}
        >
          <div style={{ marginBottom: 8, fontWeight: 600 }}>Welcome message!</div>
          <div>Supported formats: .XLSX, .CSV</div>
          <div>Dataset: OKOBAUDAT Open 2024.10</div>
          <div>Dataset Region: Europe (DE)</div>
        </div>
      </div>

      <div
        style={{
          flex: 1,
          background: "linear-gradient(180deg, #132b52 0%, #0b1833 100%)",
          padding: "36px 32px",
        }}
      >
        <div
          style={{
            maxWidth: 1100,
            margin: "0 auto",
            background: "rgba(255,255,255,0.95)",
            borderRadius: 26,
            padding: 28,
            boxShadow: "0 30px 80px rgba(0,0,0,0.25)",
            backdropFilter: "blur(6px)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: 22,
            }}
          >
            <div>
              <h1 style={{ margin: 0, fontSize: "30px", fontWeight: 700, color: "#14223b" }}>
                Results Interpretation
              </h1>
            </div>

            <button
              onClick={() => navigate("/dashboard")}
              style={{
                background: "linear-gradient(135deg, #1e40af, #2563eb)",
                color: "#ffffff",
                border: "none",
                padding: "10px 16px",
                borderRadius: 8,
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Back
            </button>
          </div>

          <div
            style={{
              background: "#edf3ff",
              borderRadius: 16,
              padding: "18px 20px",
              marginBottom: 28,
              border: "1px solid #dbe5ff",
              color: "#1f2f50",
              fontWeight: 500,
              boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
            }}
          >
            1 Analyzing the results for the functional unit using EN 15978 boundary.
          </div>

          <div
            style={{
              background: "#ffffff",
              borderRadius: 18,
              padding: 20,
              border: "1px solid #e6ecf5",
              boxShadow: "0 10px 28px rgba(0,0,0,0.06)",
              marginBottom: 24,
            }}
          >
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#14223b" }}>
                Example Project Overview
              </div>
            </div>
            <div style={{ display: "flex", gap: 18 }}>
              <div
                style={{
                  flex: 1,
                  background: "#f8fbff",
                  borderRadius: 16,
                  padding: 20,
                  border: "1px solid #e3ecff",
                  boxShadow: "0 6px 16px rgba(0,0,0,0.05)",
                }}
              >
                <h3 style={{ marginTop: 0, marginBottom: 8, color: "#23395d" }}>
                  Data Quality & Coverage
                </h3>
                <div style={{ fontSize: 13, color: "#64748b", marginBottom: 12 }}>
                  Governed ratio and dataset completeness from output contract
                </div>

                <div style={donutStyle}>
                  <div style={donutInnerStyle}>{governedRatioPercent}%</div>
                </div>

                <div style={{ fontSize: 14, color: "#475569", lineHeight: 1.9 }}>
                  <div>Total Records: {totalRecords}</div>
                  <div>Matched Records: {governedCount}</div>
                  <div>Excluded Records: {excludedCount}</div>
                  <div>Governed Ratio: {governedRatioPercent}% matched</div>
                </div>
              </div>

              <div
                style={{
                  flex: 1,
                  background: "#f8fbff",
                  borderRadius: 16,
                  padding: 20,
                  border: "1px solid #e3ecff",
                  boxShadow: "0 6px 16px rgba(0,0,0,0.05)",
                }}
              >
                <h3 style={{ marginTop: 0, marginBottom: 16, color: "#23395d" }}>
                  GWP Summary
                </h3>

                <div style={{ fontSize: 13, color: "#64748b", marginBottom: 6 }}>
                  Total A1-A3
                </div>

                <div
                  style={{
                    fontSize: 34,
                    fontWeight: 700,
                    letterSpacing: "-0.5px",
                    color: "#14223b",
                    marginBottom: 18,
                  }}
                >
                  {totalA13Display} kgCO₂e
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 12,
                    marginBottom: 16,
                  }}
                >
                  <div
                    style={{
                      background: "#ffffff",
                      border: "1px solid #d9e2f1",
                      borderRadius: 12,
                      padding: 12,
                    }}
                  >
                    <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>Dataset</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#14223b" }}>{datasetLabel}</div>
                  </div>

                  <div
                    style={{
                      background: "#ffffff",
                      border: "1px solid #d9e2f1",
                      borderRadius: 12,
                      padding: 12,
                    }}
                  >
                    <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>Governed Ratio</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#14223b" }}>{governedRatioPercent}% matched</div>
                  </div>
                </div>

                <div style={{ fontSize: 13, color: "#64748b", lineHeight: 1.8 }}>
                  Embodied carbon summary for the governed assessment boundary.
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e6ecf5",
              borderRadius: 20,
              padding: 28,
              boxShadow: "0 14px 36px rgba(0,0,0,0.08)",
            }}
          >
            {byStage.length > 0 && (
              <div
                style={{
                  marginBottom: 24,
                  background: "#f8fbff",
                  borderRadius: 16,
                  padding: 22,
                  border: "1px solid #e3ecff",
                  boxShadow: "0 6px 16px rgba(0,0,0,0.05)",
                }}
              >
                <h3 style={{ marginTop: 0, marginBottom: 14 }}>Lifecycle Stage Breakdown</h3>

                {byStage.map((row) => (
                  <div key={row.stage} style={{ marginBottom: 12 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: "13px",
                        marginBottom: 6,
                        color: "#30425c",
                      }}
                    >
                      <span>{row.stage}</span>
                      <span>
                        {Number(row.kg_co2e || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kgCO₂e ({Number(row.percentage_of_total || 0).toFixed(2)}%)
                      </span>
                    </div>

                    <div
                      style={{
                        height: 10,
                        background: "#e0e6f0",
                        borderRadius: 999,
                        overflow: "hidden",
                        width: "100%",
                      }}
                    >
                      <div
                        style={{
                          width: `${Number(row.percentage_of_total || 0)}%`,
                          height: "100%",
                          background: "#1d4ed8",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            <LCAResults outputContract={outputContract} />

            <div
              style={{
                marginTop: 24,
                background: "#f8fbff",
                borderRadius: 16,
                padding: 22,
                border: "1px solid #e3ecff",
                boxShadow: "0 6px 16px rgba(0,0,0,0.05)",
              }}
            >
              <h3 style={{ marginTop: 0, marginBottom: 14 }}>Hotspots</h3>

              {topMaterials.length > 0 ? (
                <>
                  {topMaterials.map((mat, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        marginBottom: 10,
                        padding: "10px 12px",
                        background: "#ffffff",
                        border: "1px solid #d9e2f1",
                        borderRadius: 12,
                      }}
                    >
                      <div
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          background: index === 0 ? "#dc2626" : index === 1 ? "#f59e0b" : "#3b82f6",
                          flexShrink: 0,
                        }}
                      />
                      <div style={{ fontSize: 14, color: "#30425c" }}>
                        <strong>{mat.category.charAt(0).toUpperCase() + mat.category.slice(1)}</strong> contributes {Number(mat.percentage_of_total || 0).toFixed(2)}% of total embodied carbon.
                      </div>
                    </div>
                  ))}

                  <div
                    style={{
                      marginTop: 12,
                      padding: "12px 14px",
                      background: "#ffffff",
                      border: "1px solid #d9e2f1",
                      borderRadius: 12,
                      fontSize: 14,
                      color: "#30425c",
                    }}
                  >
                    Prioritize reduction in the top three materials to achieve maximum carbon impact.
                  </div>
                </>
              ) : (
                <div>No material hotspot data available.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResultsInterpretation;